import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone';
import type { EChartsOption } from 'echarts';
import {
    TradeChartData,
    TradeKpi,
    TradeDataPoint
} from '../../models/trade-chart.model';
import { SwitchThemeService } from '../../services/switch-theme/switch-theme.service';
import { SharedService } from '../../shared.service';
import { SharedConstant } from '../../constants/sharedConstants';

@Component({
    selector: 'ems-trade-chart',
    templateUrl: './trade-chart.component.html',
    styleUrls: ['./trade-chart.component.scss']
})
export class TradeChartComponent implements OnInit, OnChanges, OnDestroy {
    private readonly ET_TIMEZONE = 'America/New_York';
    @Input() data: TradeChartData | null = null;
    @Input() loading = false;
    @Input() error = false;
    noTradeRecords = false;
    @Input() allSelected = false;
    @Input() tabs: Array<string | [string, string]> = [];
    /** Opaque key that changes whenever the active client changes (e.g. workbench cache key).
     *  A change resets the active tab to Today so the chart opens fresh for the new client. */
    @Input() clientKey = '';

    /** Emits computed { fromDate, toDate } (MM/DD/YYYY) when the user clicks Download Report. */
    @Output() downloadReport = new EventEmitter<{ fromDate: string; toDate: string }>();

    readonly sharedConstant = SharedConstant;
    activeTab = '';
    activeKpi: TradeKpi | null = null;
    chartOptions: EChartsOption = {};
    normalizedTabs: Array<[string, string]> = [];

    /** Placeholder cards shown in the empty state (no orgId selected). */
    readonly emptyKpiCards: Array<{ label: string; subtitle: string }> = [
        { label: SharedConstant.tradeChart.kpiLabels.totalAttempts, subtitle: SharedConstant.tradeChart.kpiSubtitles.vsPreviousPeriod },
        { label: SharedConstant.tradeChart.kpiLabels.allowed,       subtitle: SharedConstant.tradeChart.kpiSubtitles.percentOfTotal },
        { label: SharedConstant.tradeChart.kpiLabels.restricted,    subtitle: SharedConstant.tradeChart.kpiSubtitles.percentOfTotal },
        { label: SharedConstant.tradeChart.kpiLabels.blockRate,     subtitle: SharedConstant.tradeChart.kpiSubtitles.vsPreviousPeriod }
    ];

    private themeSub: Subscription | null = null;

    constructor(
        private switchThemeService: SwitchThemeService,
        private sharedService: SharedService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.normalizeTabs();
        this.themeSub = this.switchThemeService.isDarkMode().subscribe(() => {
            if (this.data) {
                this.renderChart(this.getChartData());
                this.cdr.detectChanges();
            }
        });
    }

    ngOnDestroy(): void {
        this.themeSub?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tabs']) {
            this.normalizeTabs();
        }
        // clientKey changes synchronously when the parent switches clients — reset the tab
        // immediately without touching chart data, so there is no stale-data flash.
        if (changes['clientKey'] && !changes['clientKey'].firstChange) {
            this.activeTab = this.normalizedTabs[0]?.[0] || '';
            this.activeKpi = null;
        }
        if (changes['data'] && this.data) {
            if (!this.getPeriodKpi(this.activeTab)) {
                this.activeTab = this.normalizedTabs[0]?.[0] || this.activeTab;
            }
            this.activeKpi = this.getPeriodKpi(this.activeTab);
            this.renderChart(this.getChartData());
        }
    }

    onTabChange(range: string): void {
        if (!this.normalizedTabs.some(([key]) => key === range)) {
            return;
        }
        this.activeTab = range;
        if (this.data) {
            this.activeKpi = this.getPeriodKpi(range);
            // Today tab uses hourly data under key "1"; all other tabs use
            // root-level tradeAttemptsPeriodicCountInfo filtered by selected days.
            this.renderChart(this.getChartData());
        }
    }

    onDownloadReport(): void {
        const dayWindow = this.getDownloadDayWindow();
        if (dayWindow <= 0) {
            return;
        }
        const daysBefore = dayWindow - 1;

        this.downloadReport.emit({
            fromDate: this.getEasternDateMMDDYYYY(daysBefore),
            toDate:   this.getEasternDateMMDDYYYY(0)
        });
    }

    /** Returns today's date in US Eastern Time (America/New_York), minus offsetDays, formatted MM/DD/YYYY. */
    private getEasternDateMMDDYYYY(offsetDays: number): string {
        return this.getEasternNow().subtract(offsetDays, 'days').format('MM/DD/YYYY');
    }

    private getEasternNow(): moment.Moment {
        return moment().tz(this.ET_TIMEZONE);
    }

    formatDifference(value: string): string {
        const num = parseFloat(value);
        const prefix = num > 0 ? '+' : '';
        return `${prefix}${value}%`;
    }

    formatPercent(value: string): string {
        return `${value}%`;
    }

    isPositive(value: string): boolean {
        return parseFloat(value) > 0;
    }

    isNegative(value: string): boolean {
        return parseFloat(value) < 0;
    }

    private getChartData(): TradeDataPoint[] {
        if (!this.data) { return []; }
        if (!this.activeTab) { return []; }
        const dayWindow = this.getDayWindow(this.activeTab);
        if (dayWindow === 1) {
            const hourly = this.getPeriodKpi('1')?.tradeAttemptsHourlyCountInfo || [];
            return [...hourly].reverse();
        }
        const all = this.data.tradeAttemptsPeriodicCountInfo || [];
        return [...this.filterByDays(all, dayWindow)].reverse();
    }

    private normalizeTabs(): void {
        // Accept either tuple tabs ([key, label]) or plain labels from parent inputs.
        const source = this.tabs && this.tabs.length
            ? this.tabs
            : [];

        this.normalizedTabs = source.map((tab) => {
            if (Array.isArray(tab)) {
                return [String(tab[0]), String(tab[1])] as [string, string];
            }
            const label = String(tab).trim();
            // Map Today to backend key "1" so chart lookup and tab label stay consistent.
            if (label.toLowerCase() === 'today') {
                return ['1', 'Today'];
            }
            // Normalize labels like "7 Days" to ["7", "7 Days"] for stable keying.
            const dayMatch = label.match(/^(\d+)\s*days?$/i)?.[1];
            const period = this.sharedService.parsePeriodInDays(dayMatch);
            if (period !== null) {
                return [String(period), this.sharedService.formatPeriodLabel(period)];
            }
            return [label, label];
        });

        // Default to the first available tab only when no tab is already selected.
        if (!this.activeTab && this.normalizedTabs.length) {
            this.activeTab = this.normalizedTabs[0][0];
        }
    }

    private resolvePeriod(tabKey: string): number | null {
        const dataDays = this.getPeriodKpi(tabKey)?.periodInDays;
        if (typeof dataDays === 'number' && Number.isInteger(dataDays) && dataDays > 0) {
            return dataDays;
        }
        return this.sharedService.parsePeriodInDays(tabKey);
    }

    private getDayWindow(tabKey: string): number {
        // Fallback: derive the largest available period from tabs/data.
        return this.resolvePeriod(tabKey) ?? this.getLargestAvailablePeriod();
    }

    private getDownloadDayWindow(): number {
        return this.resolvePeriod(this.activeTab) ?? 0;
    }

    /**
     * Derives the largest numeric period (in days) available across tab keys and payload keys.
     * Used as a final fallback when no explicit period can be resolved for the active tab.
     */
    private getLargestAvailablePeriod(): number {
        // Candidate periods from normalized tab keys (e.g., "1", "7", "30").
        const periodsFromTabs = this.normalizedTabs
            .map(([key]) => this.sharedService.parsePeriodInDays(key))
            .filter((period): period is number => period !== null);

        // Candidate periods from top-level data keys when payload tabs are dynamic.
        const periodsFromData = this.data
            ? Object.keys(this.data)
                .map((key) => this.sharedService.parsePeriodInDays(key))
                .filter((period): period is number => period !== null)
            : [];

        // Ignore non-positive values; return 0 when no valid period exists.
        const allPeriods = [...periodsFromTabs, ...periodsFromData].filter((period) => period > 0);
        return allPeriods.length ? Math.max(...allPeriods) : 0;
    }

    private getPeriodKpi(tabKey: string): TradeKpi | null {
        if (!this.data) {
            return null;
        }
        const tabData = this.data[tabKey];
        return this.isTradeKpi(tabData) ? tabData : null;
    }

    private isTradeKpi(value: TradeKpi | TradeDataPoint[] | string | undefined): value is TradeKpi {
        return !!value
            && typeof value === 'object'
            && !Array.isArray(value)
            && typeof value.periodInDays === 'number';
    }

    private filterByDays(points: TradeDataPoint[], days: number): TradeDataPoint[] {
        if (!points.length) { return []; }
        // Keep chart windows aligned with report ranges, which are based on ET.
        const cutoff = this.getEasternNow().subtract(days - 1, 'days').startOf('day').valueOf();
        return points.filter((p) => {
            const parsed = this.parseTradeDate(p.date);
            return parsed.isValid() && parsed.valueOf() >= cutoff;
        });
    }

    private parseTradeDate(dateStr: string): moment.Moment {
        return moment.tz(
            dateStr,
            ['DD-MMM-YYYY HH:mm:ss', 'DD-MMM-YYYY', moment.ISO_8601],
            true,
            this.ET_TIMEZONE
        );
    }

    private formatAxisDate(dateStr: string): string {
        const parsed = this.parseTradeDate(dateStr);
        return parsed.isValid() ? parsed.format('MMM D') : dateStr; // e.g. "May 13"
    }

    private getCssVar(name: string): string {
        return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    }

    private renderChart(series: TradeDataPoint[]): void {
        this.noTradeRecords = series.length === 0;
        const isToday = this.activeTab === '1';
        const dates = isToday
            ? series.map(d => d.date)
            : series.map(d => this.formatAxisDate(d.date));
        const allowedData = series.map(d => d.allowed);
        const restrictedData = series.map(d => d.restricted);

        const textPrimary = this.getCssVar('--text-primary');
        const allowedColor = this.getCssVar('--workbench-green');
        const restrictedColor = this.getCssVar('--workbench-attempt-trade');
        const tooltipBg = this.getCssVar('--surface-overlay');
        const tooltipBorder = this.getCssVar('--surface-border');
        const appFont = this.getCssVar('--font-family-bold').replace('-Bold', '-Regular') || 'CharlesModern-Regular, Arial, Helvetica, sans-serif';

        const gridLineColor = this.getCssVar('--surface-border-light');

        // Dynamically compute interval so at most 6 labels appear on the x-axis.
        // ECharts interval is 0-based: 0 = show every label, 1 = every other, etc.
        // When there are 6 or fewer data points show them all; otherwise space evenly.
        const maxLabels = 7;
        const labelInterval = series.length <= maxLabels
            ? 0
            : Math.ceil(series.length / maxLabels) - 1;

        // When there is only one data point there is no line segment to draw,
        // so force symbols visible so the single point is not invisible.
        const showSymbol = series.length === 1;

        this.chartOptions = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
                backgroundColor: tooltipBg,
                borderColor: tooltipBorder,
                textStyle: { color: textPrimary, fontFamily: appFont, fontSize: 12 }
            },
            grid: {
                left: '55px',
                right: '20px',
                top: '20px',
                bottom: '65px',
                containLabel: false
            },
            xAxis: {
                type: 'category',
                data: dates,
                name: isToday ? 'HOUR' : 'DATE',
                nameLocation: 'middle',
                nameGap: 35,
                nameTextStyle: { fontWeight: 'bold', fontSize: 11, color: textPrimary, fontFamily: appFont },
                axisLabel: {
                    interval: labelInterval,
                    rotate: 0,
                    fontSize: 11,
                    color: textPrimary,
                    fontFamily: appFont
                }
            },
            yAxis: {
                type: 'value',
                name: 'TRADE VOLUME',
                nameLocation: 'middle',
                nameRotate: 90,
                nameGap: 45,
                nameTextStyle: { fontWeight: 'bold', fontSize: 11, color: textPrimary, fontFamily: appFont },
                minInterval: 1,
                axisLabel: { color: textPrimary, fontFamily: appFont },
                splitLine: { lineStyle: { color: gridLineColor } }
            },
            series: [
                {
                    name: 'Allowed',
                    type: 'line',
                    data: allowedData,
                    showSymbol,
                    areaStyle: { opacity: 0.1 },
                    itemStyle: { color: allowedColor },
                    lineStyle: { color: allowedColor, width: 2 }
                },
                {
                    name: 'Restricted',
                    type: 'line',
                    data: restrictedData,
                    showSymbol,
                    areaStyle: { opacity: 0.1 },
                    itemStyle: { color: restrictedColor },
                    lineStyle: { color: restrictedColor, width: 2 }
                }
            ]
        };
    }
}
